import { forwardRef, useRef, useEffect, useState } from 'react';
import type { PropsWithoutRef } from 'react';
import styled from 'styled-components';
import type { Editor as TiptapEditor } from '@tiptap/core';

import {
  Button,
  defaultThemeProp,
  isInstance,
  Menu,
  menuHelpers,
  Modal,
  Popover,
  StyledPopover,
  useModalManager,
  useConsolidatedRef,
  useI18n,
  useUID,
  useTestIds,
  withTestIds,
  registerIcon
} from '@pega/cosmos-react-core';
import type {
  ForwardRefForwardPropsComponent,
  ModalMethods,
  PropsWithDefaults
} from '@pega/cosmos-react-core';
import * as codeIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/code.icon';

import { getKeyCommand } from '../RichTextEditor/Toolbar/utils';
import { Editor } from '../Editor';
import type { EditorProps } from '../Editor';
import type { LinkCustomSourceEditValue } from '../Editor/Editor.types';

import { isFieldMenuItem } from './DynamicContentEditor.types';
import type { DynamicContentEditorProps, FieldItems, ItemType } from './DynamicContentEditor.types';
import { pegaReferenceElementStyle, createPegaReferenceElement } from './PegaCustomElement';
import { getDynamicContentEditorTestIds } from './DynamicContentEditor.test-ids';

registerIcon(codeIcon);

type DynamicContentEditorPropsWithDefaults = PropsWithDefaults<DynamicContentEditorProps>;

const StyledDynamicContentEditor = styled.div<DynamicContentEditorPropsWithDefaults>``;

export const StyledSearchPopover = styled(StyledPopover)`
  min-width: 20rem;
`;

StyledDynamicContentEditor.defaultProps = defaultThemeProp;

const DynamicContentEditor: ForwardRefForwardPropsComponent<DynamicContentEditorProps> = forwardRef(
  function DynamicContentEditor(
    {
      testId,
      form: { dynamicContentPicker, onSubmit },
      onActiveFieldChange,
      label,
      toolbar,
      fieldItems,
      defaultValue,
      onBlur,
      onKeyDown: onKeyDownProp,
      customActions = [],
      customComponents = [],
      linkCustomSource: linkCustomSourceProp,
      ...restProps
    }: PropsWithoutRef<DynamicContentEditorProps>,
    ref: DynamicContentEditorProps['ref']
  ) {
    const testIds = useTestIds(testId, getDynamicContentEditorTestIds);
    const menuID = useUID();
    const { create } = useModalManager();
    const t = useI18n();

    const modalMethods = useRef<ModalMethods>();
    const [editor, setEditor] = useState<TiptapEditor | null>(null);
    const [fieldMenuItems, setFieldMenuItems] = useState<FieldItems[]>(fieldItems || []);
    const [currentElementId, setCurrentElementId] = useState('');
    const [currentElementContent, setCurrentElementContent] = useState('');
    const [currentElementNamespace, setCurrentElementNamespace] = useState('');
    const [currentCursorPosition, setCurrentCursorPosition] = useState({
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
      x: 0,
      y: 0,
      toJSON: () => {}
    });
    const [showFieldsPopover, setShowFieldsPopover] = useState<boolean>(false);
    const [editLinkCustomSource, setEditLinkCustomSource] =
      useState<LinkCustomSourceEditValue | null>(null);

    const fieldPopoverRef = useRef<HTMLDivElement>(null);
    const editorRef = useConsolidatedRef(ref);

    const tooltip = getKeyCommand(
      navigator.appVersion.includes('Mac'),
      ({ ctrl }) => `${t('rte_insert_field')} (${ctrl}/)`
    );

    const insertField = (field: ItemType) => {
      if (!editor) return;

      const fieldContent = `<pega-reference role="button" contenteditable="false" data-rule-type='field' data-rule-id=${
        field.id
      } ${field.namespace && `data-rule-namespace=${field.namespace}`}>${
        field.text
      }</pega-reference>`;

      editor.chain().focus().insertContent(fieldContent).run();
      modalMethods.current?.dismiss();
    };

    const modalProps = {
      'data-testid': testIds.insertFieldModal,
      children: dynamicContentPicker,
      heading: t('rte_insert_field'),
      actions: (
        <>
          <Button
            data-testid={testIds.insertFieldModalCancel}
            variant='secondary'
            onClick={() => modalMethods.current?.dismiss()}
          >
            {t('cancel')}
          </Button>
          <Button
            data-testid={testIds.insertFieldModalSubmit}
            variant='primary'
            onClick={() => onSubmit(insertField)}
          >
            {t('submit')}
          </Button>
        </>
      ),
      center: true,
      onAfterClose: () => {
        setCurrentElementId('');
        setCurrentElementContent('');
        setCurrentElementNamespace('');
        onActiveFieldChange({ id: '', text: '', namespace: '' });
      },
      onBeforeOpen: () => {
        if (currentElementId) {
          onActiveFieldChange({
            id: currentElementId,
            text: currentElementContent,
            namespace: currentElementNamespace
          });
          return;
        }
        onActiveFieldChange({ id: '', text: '', namespace: '' });
      }
    };

    const findAndRenameField = (fieldId: string) => {
      if (!editor || !fieldMenuItems) return;

      const selectedField = menuHelpers.getItem(fieldMenuItems, fieldId);
      if (selectedField && isFieldMenuItem(selectedField)) {
        editor
          .chain()
          .focus()
          .insertContent(
            `<pega-reference role="button" contenteditable="false" data-rule-type="field" data-rule-id="${
              selectedField.id
            }" ${selectedField.namespace && `data-rule-namespace=${selectedField.namespace}`}>${
              selectedField.primary
            }</pega-reference>`
          )
          .run();
        setShowFieldsPopover(false);
      }
    };

    const hidePopover = () => {
      if (fieldPopoverRef) setShowFieldsPopover(false);
    };

    const filterFieldMenu = (searchString: string) => {
      setShowFieldsPopover(true);
      setFieldMenuItems(fieldItems || []);
      if (/\s/.test(searchString)) {
        setShowFieldsPopover(false);
        return;
      }
      if (searchString && fieldMenuItems) {
        const newFieldMenuItems: FieldItems[] = menuHelpers
          .flatten(fieldMenuItems)
          .filter(({ primary }) =>
            primary.toLowerCase().startsWith(searchString.toLocaleLowerCase())
          );
        if (newFieldMenuItems) {
          setShowFieldsPopover(true);
          setFieldMenuItems(newFieldMenuItems);
        } else setShowFieldsPopover(false);
      }
    };

    const updateCurrentElement = (targetEl: HTMLElement) => {
      const fieldId = targetEl.getAttribute('data-rule-id');
      const fieldNamespace = targetEl.getAttribute('data-rule-namespace');
      const fieldValue = targetEl.innerText;
      setCurrentElementId(fieldId || '');
      setCurrentElementContent(fieldValue);
      setCurrentElementNamespace(fieldNamespace || '');
    };

    const parsePegaReferenceFromHref = (href: string) => {
      const decodedHref = href
        .replaceAll('&quot;', '"')
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&amp;', '&');
      if (!decodedHref.includes('<pega-reference')) {
        return null;
      }

      const idMatch = /data-rule-id=["']([^"']+)["']/i.exec(decodedHref);
      if (!idMatch) {
        return null;
      }

      const namespaceMatch = /data-rule-namespace=["']([^"']+)["']/i.exec(decodedHref);
      return {
        id: idMatch[1],
        namespace: namespaceMatch?.[1]
      };
    };

    const getClickableElement = (target: EventTarget | null) => {
      if (isInstance(target, HTMLElement)) {
        return target;
      }
      return null;
    };

    const handleElementClick = (event: MouseEvent) => {
      const { target } = event;

      const clickedElement = getClickableElement(target);
      if (!clickedElement) {
        return;
      }

      if (editor && target && clickedElement.tagName === 'A') {
        event.preventDefault();
        const href = clickedElement.getAttribute('href') ?? '';
        const parsedReference = parsePegaReferenceFromHref(href);
        const propertyFromHref = parsedReference?.id;
        const namespaceFromHref = parsedReference?.namespace;
        const propertyFromMalformedHref = clickedElement.getAttribute('data-rule-id');
        const namespaceFromMalformedHref =
          clickedElement.getAttribute('data-rule-namespace') || undefined;
        const property = propertyFromHref || propertyFromMalformedHref;
        const namespace = namespaceFromHref || namespaceFromMalformedHref;

        if (property) {
          const field = {
            id: property,
            text: clickedElement.innerText,
            namespace
          };
          setEditLinkCustomSource(field);
          onActiveFieldChange(field);
        }
      } else if (clickedElement.tagName === 'PEGA-REFERENCE') {
        if (clickedElement.getAttribute('data-link-source') === 'link') {
          const field = {
            id: clickedElement.getAttribute('data-rule-id') ?? '',
            text: clickedElement.innerText,
            namespace: clickedElement.getAttribute('data-rule-namespace') || undefined
          };
          setEditLinkCustomSource(field);
          onActiveFieldChange(field);
        } else {
          updateCurrentElement(clickedElement);
        }
      }
      hidePopover();
    };

    const onInsertField = () => {
      if (!editor) return;

      // Get the DOM element at the current selection
      const { from } = editor.state.selection;
      const domAtPos = editor.view.domAtPos(from);
      const targetEl = domAtPos.node.parentElement;

      if (targetEl?.tagName === 'PEGA-REFERENCE') {
        updateCurrentElement(targetEl);
      } else {
        modalMethods.current = create(Modal, modalProps);
      }
    };

    const isPegaRefActive = () => {
      if (!editor) return false;

      // Check if the selected node is a pega-reference element
      const { $from } = editor.state.selection;
      return $from.parent.type.name === 'pega-reference';
    };

    const fieldSelectionPopover = (
      <Popover
        show={showFieldsPopover}
        strategy='fixed'
        ref={fieldPopoverRef}
        aria-label={t('rte_insert_field')}
        target={{
          getBoundingClientRect() {
            return currentCursorPosition;
          }
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: () => {
                const iframe = editorRef.current?.element?.querySelector('iframe');
                const iframeRect = iframe?.getBoundingClientRect();
                const x = iframeRect?.x ?? 0;
                const y = iframeRect?.y ?? 0;
                return [x, y] satisfies [number, number];
              }
            }
          }
        ]}
        placement='bottom-start'
      >
        {fieldMenuItems && (
          <Menu
            id={menuID}
            items={fieldMenuItems}
            as={StyledSearchPopover}
            focusControlEl={editor?.view.dom || undefined}
            mode='action'
            onItemClick={findAndRenameField}
            variant='drill-down'
          />
        )}
      </Popover>
    );

    useEffect(() => {
      if (currentElementId && currentElementContent)
        modalMethods.current = create(Modal, modalProps);
    }, [currentElementId, currentElementContent]);

    useEffect(() => {
      if (modalMethods.current) {
        modalMethods.current.update(modalProps);
      }
    }, [modalProps]);

    // Set up click listener on iframe document for pega-reference interactions
    useEffect(() => {
      const iframe = editorRef.current?.element?.querySelector('iframe');
      const iframeDoc = iframe?.contentDocument;
      if (!iframeDoc) return;

      const clickListener = (e: MouseEvent) => {
        handleElementClick(e);
      };

      iframeDoc.addEventListener('click', clickListener);

      return () => {
        iframeDoc.removeEventListener('click', clickListener);
      };
    }, [editor]);

    const onEditorChange: EditorProps['onChange'] = (formEditor?: TiptapEditor) => {
      if (formEditor) {
        // Get cursor position from Tiptap editor
        const { selection } = formEditor.state;
        const { from } = selection;

        // Get the coordinates of the cursor position
        const coords = formEditor.view.coordsAtPos(from);

        setCurrentCursorPosition({
          bottom: coords.bottom,
          height: coords.bottom - coords.top,
          left: coords.left,
          right: coords.right,
          top: coords.top,
          width: coords.right - coords.left,
          x: coords.left,
          y: coords.top,
          toJSON: () => {}
        });

        if (selection.empty) {
          // Get text before cursor to check for @ mentions
          const textBefore = formEditor.state.doc.textBetween(
            Math.max(0, from - 50), // Look back up to 50 characters
            from,
            ' '
          );

          // Get the last word (after last space)
          const words = textBefore.split(' ');
          const lastWord = words[words.length - 1];

          if (lastWord?.startsWith('@') && lastWord.indexOf('@') === lastWord.lastIndexOf('@')) {
            filterFieldMenu(lastWord.slice(1));
          } else {
            setShowFieldsPopover(false);
          }
        }
      }
    };

    const onKeyDown: EditorProps['onKeyDown'] = event => {
      if (onKeyDownProp) {
        onKeyDownProp(event);
      }

      if (
        (event?.key === 'Enter' ||
          event?.key === 'ArrowLeft' ||
          event?.key === 'ArrowRight' ||
          event?.key === 'ArrowDown' ||
          event?.key === 'ArrowUp') &&
        !!fieldPopoverRef.current
      ) {
        event?.preventDefault();
      }
      if (event?.key === 'Escape' && !!fieldPopoverRef.current) {
        hidePopover();
      }
    };

    return (
      <Editor
        data-testid={testIds.root}
        ref={editorRef}
        {...restProps}
        preserveTableStyles
        label={label}
        toolbar={toolbar}
        customComponents={[
          {
            createCustomElement: createPegaReferenceElement,
            name: 'pega-reference',
            extensionAttributes: ['contenteditable', 'data-link-source'],
            style: pegaReferenceElementStyle
          },
          ...customComponents
        ]}
        customActions={[
          {
            'data-testid': testIds.insertField,
            icon: 'code',
            text: tooltip,
            onMouseDown: () => {
              modalMethods.current = create(Modal, modalProps);
            },
            active: isPegaRefActive,
            shortcut: {
              pattern: 'meta+191',
              description: tooltip,
              command: onInsertField
            }
          },
          ...customActions
        ]}
        onInit={setEditor}
        onChange={onEditorChange}
        onBlur={() => {
          onBlur?.();
          hidePopover();
        }}
        onKeyDown={onKeyDown}
        defaultValue={defaultValue}
        linkCustomSource={{
          ...linkCustomSourceProp,
          edit: editLinkCustomSource ?? linkCustomSourceProp?.edit,
          onDismissEdit: reason => {
            if (reason !== 'apply' && editLinkCustomSource) {
              onActiveFieldChange(editLinkCustomSource);
            }
            setEditLinkCustomSource(null);
            linkCustomSourceProp?.onDismissEdit?.(reason);
          }
        }}
      >
        {fieldSelectionPopover}
      </Editor>
    );
  }
);

export default withTestIds(DynamicContentEditor, getDynamicContentEditorTestIds);
